import { z } from "zod";
import { assistantStateSchema,assistantSummarySchema,assistantReviewSchema,assistantPolicySchema,assistantCommandSchema,type AssistantPolicy } from '../contracts/assistant.ts';
import {
  supportFeaturesHeader,
  unansweredThresholdFeature,
} from "../contracts/features.ts";
import {
  connectorKeyStateSchema,
  connectorKeyIssuedSchema,
  connectorKeyCommandSchema,
  admissionBudgetSchema,
  admissionUpdateSchema,
  holidayUpdateSchema,
  projectHolidaysSchema,
  downloadUsageSchema,
  jobUsageSchema,
  attachmentUsageSchema,
  messageUsageSchema,
  conversationSchema,
  messageSchema,
  eventSchema,
  preparedExportSchema,
  supportExportSchema,
  exportFileSchema,
  deletionPreviewCommandSchema,
  deletionCreateCommandSchema,
  deletionRequestCommandSchema,
  deletionListQuerySchema,
  deletionListSchema,
  type DeletionListQuery,
  type DeletionList,
  privacyStatusSchema,
  privacyGrantStateSchema,
  privacyGrantQuerySchema,
  privacyPolicyCommandSchema,
  privacyGrantCommandSchema,
  type PrivacyStatus,
  type PrivacyGrantState,
  type PrivacyPolicyCommand,
  type PrivacyGrantCommand,
  deletionReviewCommandSchema,
  deletionReconcileSchema,
  type DeletionReconcileCommand,
  deletionCancelCommandSchema,
  deletionPreviewSchema,
  deletionRequestSchema,
  type DeletionPreview,
  type DeletionRequest,
  type DeletionReviewCommand,
  deletionAuthPrepareSchema,
  deletionAuthConfirmSchema,
  deletionAuthCommandSchema,
  preparedDeletionAuthSchema,
  confirmedDeletionAuthSchema,
  type PreparedDeletionAuth,
  type ConfirmedDeletionAuth,
  deletionJobSchema,
  deletionFinalizeSchema,
  type DeletionJob,
  type PreparedExport,
  type SupportExport,
  SupportError,
  type Conversation,
  type StoredMessage,
  type InboxFilters,
} from "../contracts/index.ts";
export interface SupportSession {
  accessToken: string;
  expiresAt: string;
  projectId: string;
  actorId?: string | undefined;
}
export interface ClientOptions {
  /** Opt in only when the server advertises the optional Project field. */
  responseFeatures?: readonly (typeof unansweredThresholdFeature)[];
  apiBase: string;
  projectKey: string;
  getSession: () => Promise<SupportSession>;
  fetcher?: typeof fetch;
  /** Only the customer widget enables bounded renewal of a still-valid session. */
  renewCustomerSession?: boolean;
}
export interface PendingMessage {
  clientMessageId: string;
  text: string;
  attachmentIds: string[];
  state: "sending" | "failed" | "pending" | "stored";
}
export class AttachmentScanPending extends SupportError {
  constructor(public readonly attachmentId: string) {
    super(503, "attachment_scan_pending");
  }
}
export interface AttachmentDraft {
  id: string;
  name: string;
  state: "ready" | "scan-pending";
}
export class SupportClient {
  async assistantState(conversationId:string){return assistantStateSchema.parse(await this.request(`/conversations/${z.uuid().parse(conversationId)}/assistant/state`));}
  async requestHuman(conversationId:string,expectedVersion:number){return assistantStateSchema.parse(await this.request(`/conversations/${z.uuid().parse(conversationId)}/assistant/human`,'POST',assistantCommandSchema.parse({expectedVersion})));}
  async takeOver(conversationId:string,expectedVersion:number){return assistantStateSchema.parse(await this.request(`/conversations/${z.uuid().parse(conversationId)}/assistant/takeover`,'POST',assistantCommandSchema.parse({expectedVersion})));}
  async assistantSummary(conversationId:string){return assistantSummarySchema.parse(await this.request(`/conversations/${z.uuid().parse(conversationId)}/assistant/summary`));}
  async assistantReviews(){return z.array(assistantReviewSchema).max(50).parse(await this.request('/admin/assistant-reviews'));}
  async assistantPolicy(){return assistantPolicySchema.parse(await this.request('/admin/assistant-policy'));}
  async saveAssistantPolicy(policy:AssistantPolicy){return assistantPolicySchema.parse(await this.request('/admin/assistant-policy','PUT',assistantPolicySchema.parse(policy)));}
  private session: SupportSession | null = null;
  private readonly fetcher: typeof fetch;
  private sessionFlight: Promise<SupportSession> | null = null;
  private sessionEpoch = 0;
  constructor(private readonly options: ClientOptions) {
    this.fetcher = options.fetcher ?? globalThis.fetch.bind(globalThis);
  }
  private async ensureSession(): Promise<SupportSession> {
    if (this.session && Date.parse(this.session.expiresAt) > Date.now() + 60000)
      return this.session;
    if (!this.sessionFlight) {
      const epoch = this.sessionEpoch;
      const flight: Promise<SupportSession> = (async () => {
        let next: SupportSession;
        if (
          this.options.renewCustomerSession &&
          this.session &&
          Date.parse(this.session.expiresAt) > Date.now()
        ) {
          const response = await this.fetcher(this.url("/session/refresh"), {
            method: "POST",
            cache: "no-store",
            headers: {
              authorization: `Bearer ${this.session.accessToken}`,
              "content-type": "application/json",
            },
            body: "{}",
          });
          if (!response.ok)
            throw new SupportError(response.status, "session_expired");
          next = z
            .object({
              accessToken: z.string(),
              expiresAt: z.iso.datetime(),
              projectId: z.uuid(),
              actorId: z.uuid().optional(),
            })
            .parse(await response.json());
        } else next = await this.options.getSession();
        if (epoch !== this.sessionEpoch)
          throw new SupportError(401, "session_changed");
        this.session = next;
        return next;
      })()
        .catch((error: unknown) => {
          if (epoch !== this.sessionEpoch)
            throw new SupportError(401, "session_changed");
          throw error;
        })
        .finally(() => {
          if (this.sessionFlight === flight) this.sessionFlight = null;
        });
      this.sessionFlight = flight;
    }
    return this.sessionFlight;
  }
  private url(path: string): URL {
    const url = new URL(
      `${this.options.apiBase}${path}`,
      typeof location === "undefined"
        ? "https://support.invalid"
        : location.href,
    );
    url.searchParams.set("projectKey", this.options.projectKey);
    return url;
  }
  async request(
    path: string,
    method = "GET",
    input?: unknown,
  ): Promise<unknown> {
    const epoch = this.sessionEpoch;
    const session = await this.ensureSession();
    if (epoch !== this.sessionEpoch)
      throw new SupportError(401, "session_changed");
    const response = await this.fetcher(this.url(path), {
      method,
      cache: "no-store",
      headers: {
        authorization: `Bearer ${session.accessToken}`,
        ...(this.options.responseFeatures?.includes(unansweredThresholdFeature)
          ? { [supportFeaturesHeader]: unansweredThresholdFeature }
          : {}),
        ...(input === undefined ? {} : { "content-type": "application/json" }),
      },
      ...(input === undefined ? {} : {body: JSON.stringify(input)}),
    });
    const value: unknown = await response.json();
    if (epoch !== this.sessionEpoch)
      throw new SupportError(401, "session_changed");
    if (!response.ok) {
      const parsed = z
        .object({ error: z.string(), current: z.unknown().optional() })
        .parse(value);
      if (response.status === 401) this.session = null;
      throw new SupportError(response.status, parsed.error, parsed.current);
    }
    return value;
  }
  async listConversations(
    allProjects = false,
    filters: InboxFilters = {},
  ): Promise<Conversation[]> {
    const query = new URLSearchParams();
    for (const [key,value] of Object.entries(filters)) if(value !== undefined) query.set(key,value);
    if(allProjects) query.set('allProjects','true');
    return z
      .array(conversationSchema)
      .parse(
        await this.request(
          `/conversations${query.size ? "?" + query.toString() : ""}`,
        ),
      );
  }
  async inboxSummary(
    allProjects = false,
  ): Promise<{ totalCount: number; unreadCount: number }> {
    return z
      .object({
        totalCount: z.number().int().nonnegative(),
        unreadCount: z.number().int().nonnegative(),
      })
      .parse(
        await this.request(
          `/inbox-summary${allProjects ? "?allProjects=true" : ""}`,
        ),
      );
  }
  async createConversation(title: string): Promise<Conversation> {
    return conversationSchema.parse(
      await this.request("/conversations", "POST", { title }),
    );
  }
  async getConversation(conversationId: string): Promise<Conversation> {
    return conversationSchema.parse(await this.request(`/conversations/${z.uuid().parse(conversationId)}`));
  }
  async previewDeletion(conversationId: string): Promise<DeletionPreview> {
    const command = deletionPreviewCommandSchema.parse({ conversationId }),
      epoch = this.sessionEpoch;
    const result = await this.request(
      `/admin/deletion-preview?conversationId=${command.conversationId}`,
    );
    if (epoch !== this.sessionEpoch)
      throw new SupportError(401, "session_changed");
    return deletionPreviewSchema.parse(result);
  }
  async getPrivacyStatus(): Promise<PrivacyStatus> {
    return privacyStatusSchema.parse(
      await this.request("/admin/privacy-policy"),
    );
  }
  async savePrivacyPolicy(
    command: PrivacyPolicyCommand,
  ): Promise<PrivacyStatus> {
    return privacyStatusSchema.parse(
      await this.request(
        "/admin/privacy-policy",
        "PUT",
        privacyPolicyCommandSchema.parse(command),
      ),
    );
  }
  async getPrivacyGrant(actorId: string): Promise<PrivacyGrantState> {
    const command = privacyGrantQuerySchema.parse({ actorId });
    return privacyGrantStateSchema.parse(
      await this.request(`/admin/privacy-grants?actorId=${command.actorId}`),
    );
  }
  async savePrivacyGrant(
    command: PrivacyGrantCommand,
  ): Promise<PrivacyGrantState> {
    return privacyGrantStateSchema.parse(
      await this.request(
        "/admin/privacy-grants",
        "PUT",
        privacyGrantCommandSchema.parse(command),
      ),
    );
  }
  async createDeletionRequest(
    conversationId: string,
    expectedVersion: number,
  ): Promise<DeletionRequest> {
    return this.deletionCommand(
      "/admin/deletion-requests",
      "POST",
      deletionCreateCommandSchema.parse({ conversationId, expectedVersion }),
    );
  }
  async getDeletionRequest(requestId: string): Promise<DeletionRequest> {
    const command = deletionRequestCommandSchema.parse({ requestId });
    return this.deletionCommand(
      `/admin/deletion-requests/${command.requestId}`,
      "GET",
    );
  }
  async listDeletionRequests(
    filters: DeletionListQuery = {},
  ): Promise<DeletionList> {
    const command = deletionListQuerySchema.parse(filters),
      epoch = this.sessionEpoch;
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(command))
      if (value !== undefined) query.set(key, value);
    const result = await this.request(
      `/admin/deletion-requests${query.size ? `?${query.toString()}` : ""}`,
    );
    if (epoch !== this.sessionEpoch)
      throw new SupportError(401, "session_changed");
    return deletionListSchema.parse(result);
  }
  async reviewDeletionRequest(
    command: DeletionReviewCommand,
  ): Promise<DeletionRequest> {
    return this.deletionCommand(
      "/admin/deletion-requests/review",
      "POST",
      deletionReviewCommandSchema.parse(command),
    );
  }
  async reconcileDeletionRequest(
    command: DeletionReconcileCommand,
  ): Promise<DeletionRequest> {
    return this.deletionCommand(
      "/admin/deletion-requests/reconcile",
      "POST",
      deletionReconcileSchema.parse(command),
    );
  }
  async cancelDeletionRequest(
    requestId: string,
    expectedVersion: number,
  ): Promise<DeletionRequest> {
    return this.deletionCommand(
      "/admin/deletion-requests/cancel",
      "POST",
      deletionCancelCommandSchema.parse({ requestId, expectedVersion }),
    );
  }
  private async deletionCommand(
    path: string,
    method: string,
    input?: unknown,
  ): Promise<DeletionRequest> {
    const epoch = this.sessionEpoch,
      result = await this.request(path, method, input);
    if (epoch !== this.sessionEpoch)
      throw new SupportError(401, "session_changed");
    return deletionRequestSchema.parse(result);
  }
  async prepareDeletionAuth(
    requestId: string,
    expectedVersion: number,
  ): Promise<PreparedDeletionAuth> {
    const command = deletionAuthPrepareSchema.parse({
        requestId,
        expectedVersion,
      }),
      epoch = this.sessionEpoch;
    const result = await this.request(
      "/admin/deletion-auth/prepare",
      "POST",
      command,
    );
    if (epoch !== this.sessionEpoch)
      throw new SupportError(401, "session_changed");
    return preparedDeletionAuthSchema.parse(result);
  }
  async confirmDeletionAuth(
    authorizationId: string,
    authAccessToken: string,
  ): Promise<ConfirmedDeletionAuth> {
    const command = deletionAuthConfirmSchema.parse({
        authorizationId,
        authAccessToken,
      }),
      epoch = this.sessionEpoch;
    const result = await this.request(
      "/admin/deletion-auth/confirm",
      "POST",
      command,
    );
    if (epoch !== this.sessionEpoch)
      throw new SupportError(401, "session_changed");
    return confirmedDeletionAuthSchema.parse(result);
  }
  async cancelDeletionAuth(authorizationId: string): Promise<void> {
    await this.request(
      "/admin/deletion-auth/cancel",
      "POST",
      deletionAuthCommandSchema.parse({ authorizationId }),
    );
  }
  async executeDeletion(authorizationId: string): Promise<DeletionJob> {
    const epoch = this.sessionEpoch;
    const result = await this.request(
      "/admin/deletions/execute",
      "POST",
      deletionAuthCommandSchema.parse({ authorizationId }),
    );
    if (epoch !== this.sessionEpoch)
      throw new SupportError(401, "session_changed");
    return deletionJobSchema.parse(result);
  }
  async finalizeDeletion(
    requestId: string,
    jobVersion: number,
  ): Promise<DeletionJob> {
    const command = deletionFinalizeSchema.parse({ requestId, jobVersion }),
      epoch = this.sessionEpoch;
    const result = await this.request(
      "/admin/deletions/finalize",
      "POST",
      command,
    );
    if (epoch !== this.sessionEpoch)
      throw new SupportError(401, "session_changed");
    return deletionJobSchema.parse(result);
  }
  async getDeletionJob(requestId: string): Promise<DeletionJob> {
    const command = deletionRequestCommandSchema.parse({ requestId }),
      epoch = this.sessionEpoch;
    const result = await this.request(`/admin/deletions/${command.requestId}`);
    if (epoch !== this.sessionEpoch)
      throw new SupportError(401, "session_changed");
    return deletionJobSchema.parse(result);
  }
  async prepareExport(
    conversationId: string,
    includeInternalNotes = false,
  ): Promise<PreparedExport> {
    const epoch = this.sessionEpoch;
    const result = await this.request("/admin/exports/prepare", "POST", {
      conversationId,
      includeInternalNotes,
    });
    if (epoch !== this.sessionEpoch)
      throw new SupportError(401, "session_changed");
    return preparedExportSchema.parse(result);
  }
  async confirmExport(
    requestId: string,
    authAccessToken: string,
  ): Promise<void> {
    const epoch = this.sessionEpoch;
    await this.request("/admin/exports/confirm", "POST", {
      requestId,
      authAccessToken,
    });
    if (epoch !== this.sessionEpoch)
      throw new SupportError(401, "session_changed");
  }
  async downloadExport(requestId: string): Promise<SupportExport> {
    const epoch = this.sessionEpoch;
    const result = await this.request("/admin/exports/download", "POST", {
      requestId,
    });
    if (epoch !== this.sessionEpoch)
      throw new SupportError(401, "session_changed");
    return supportExportSchema.parse(result);
  }
  async cancelExport(requestId: string): Promise<void> {
    await this.request("/admin/exports/cancel", "POST", { requestId });
  }
  async downloadExportAttachment(
    requestId: string,
    attachmentId: string,
  ): Promise<Blob> {
    const epoch = this.sessionEpoch,
      command = exportFileSchema.parse({ requestId, attachmentId });
    const session = await this.ensureSession();
    if (epoch !== this.sessionEpoch)
      throw new SupportError(401, "session_changed");
    const response = await this.fetcher(this.url("/admin/exports/attachment"), {
      method: "POST",
      cache: "no-store",
      headers: {
        authorization: `Bearer ${session.accessToken}`,
        "content-type": "application/json",
      },
      body: JSON.stringify(command),
    });
    if (epoch !== this.sessionEpoch)
      throw new SupportError(401, "session_changed");
    if (!response.ok) {
      const error = z
        .object({ error: z.string() })
        .parse(await response.json());
      throw new SupportError(response.status, error.error);
    }
    const declared = Number(response.headers.get("x-support-file-size"));
    if (!Number.isInteger(declared) || declared < 1 || declared > 10485760)
      throw new SupportError(413, "invalid_export_file");
    const blob = await response.blob();
    if (epoch !== this.sessionEpoch)
      throw new SupportError(401, "session_changed");
    if (blob.size !== declared)
      throw new SupportError(409, "export_file_changed");
    return blob;
  }
  async send(
    conversationId: string,
    pending: PendingMessage,
  ): Promise<StoredMessage> {
    pending.state = "sending";
    try {
      const result = messageSchema.parse(
        await this.request(
          `/conversations/${conversationId}/messages`,
          "POST",
          {
            clientMessageId: pending.clientMessageId,
            text: pending.text,
            attachmentIds: pending.attachmentIds,
          },
        ),
      );
      pending.state = "stored";
      return result;
    } catch (error) {
      pending.state =
        typeof navigator !== "undefined" && Object.is(navigator.onLine, false)
          ? "pending"
          : "failed";
      throw error;
    }
  }
  async replay(
    conversationId: string,
    known: StoredMessage[] = [],
  ): Promise<StoredMessage[]> {
    const map = new Map(known.map((m) => [m.messageId, m]));
    let cursor = known.length ? Math.max(...known.map((m) => m.messageSeq)) : 0;
    // A gap requires replay from before the gap; never advance solely from WS arrival order.
    const ordered = [...known].sort((a, b) => a.messageSeq - b.messageSeq);
    for (const [i, message] of ordered.entries())
      if (message.messageSeq !== i + 1) {
        cursor = i;
        break;
      }
    let page: StoredMessage[];
    do {
      page = z
        .array(messageSchema)
        .parse(
          await this.request(
            `/conversations/${conversationId}/messages?afterSeq=${String(cursor)}`,
          ),
        );
      for (const m of page) map.set(m.messageId, m);
      cursor = page.at(-1)?.messageSeq ?? cursor;
    } while (page.length === 100);
    return [...map.values()].sort((a, b) => a.messageSeq - b.messageSeq);
  }
  async markRead(conversationId: string, lastReadSeq: number) {
    return this.request(`/conversations/${conversationId}/read-cursor`, "PUT", {
      lastReadSeq,
    });
  }
  async uploadAttachment(conversationId: string, file: File): Promise<string> {
    const epoch = this.sessionEpoch;
    const intent = z
      .object({ attachmentId: z.string() })
      .loose()
      .parse(
        await this.request("/attachments/upload-intents", "POST", {
          conversationId,
          name: file.name,
          mime: file.type,
          size: file.size,
        }),
      );
    const session = await this.ensureSession();
    if (epoch !== this.sessionEpoch)
      throw new SupportError(401, "session_changed");
    let response: Response;
    try {
      response = await this.fetcher(
        this.url(`/attachments/${intent.attachmentId}/content`),
        {
          method: "PUT",
          headers: {
            authorization: `Bearer ${session.accessToken}`,
            "content-type": file.type,
          },
          body: file,
        },
      );
    } catch {
      if (epoch !== this.sessionEpoch)
        throw new SupportError(401, "session_changed");
      throw new AttachmentScanPending(intent.attachmentId);
    }
    if (epoch !== this.sessionEpoch)
      throw new SupportError(401, "session_changed");
    if (!response.ok) {
      const failure = z
        .object({ error: z.string() })
        .safeParse(await response.json().catch(() => null));
      if (epoch !== this.sessionEpoch)
        throw new SupportError(401, "session_changed");
      if (
        response.status === 503 &&
        failure.success &&
        failure.data.error === "attachment_storage_pending"
      )
        throw new AttachmentScanPending(intent.attachmentId);
      throw new SupportError(response.status, "attachment_upload_failed");
    }
    if (epoch !== this.sessionEpoch)
      throw new SupportError(401, "session_changed");
    return this.completeAttachment(intent.attachmentId);
  }
  async completeAttachment(attachmentId: string): Promise<string> {
    const epoch = this.sessionEpoch;
    let value: unknown;
    try {
      value = await this.request(
        `/attachments/${attachmentId}/complete`,
        "POST",
        {},
      );
    } catch (error) {
      if (epoch !== this.sessionEpoch)
        throw new SupportError(401, "session_changed");
      if (!(error instanceof SupportError) || error.status >= 500)
        throw new AttachmentScanPending(attachmentId);
      throw error;
    }
    if (epoch !== this.sessionEpoch)
      throw new SupportError(401, "session_changed");
    const result = z
      .object({ status: z.enum(["ready", "rejected"]) })
      .parse(value);
    if (result.status !== "ready")
      throw new SupportError(403, "attachment_rejected");
    return attachmentId;
  }
  async downloadAttachment(attachmentId: string): Promise<Blob> {
    const epoch = this.sessionEpoch;
    const session = await this.ensureSession();
    if (epoch !== this.sessionEpoch)
      throw new SupportError(401, "session_changed");
    const response = await this.fetcher(
      this.url(`/attachments/${attachmentId}/download`),
      {
        cache: "no-store",
        headers: { authorization: `Bearer ${session.accessToken}` },
      },
    );
    if (epoch !== this.sessionEpoch)
      throw new SupportError(401, "session_changed");
    if (!response.ok)
      throw new SupportError(response.status, "attachment_download_failed");
    const blob = await response.blob();
    if (epoch !== this.sessionEpoch)
      throw new SupportError(401, "session_changed");
    return blob;
  }
  subscribe(
    conversationId: string,
    onSync: () => void,
    onState: (state: "connecting" | "connected" | "reconnecting") => void,
  ): () => void {
    return this.subscribeChannel(
      "/ws-tickets",
      "/realtime",
      { conversationId },
      onSync,
      onState,
      (value) => eventSchema.safeParse(value).success,
    );
  }
  subscribeInbox(
    onSync: () => void,
    onState: (
      state: "connecting" | "connected" | "reconnecting",
    ) => void = () => undefined,
  ): () => void {
    const signal = z
      .object({
        type: z.literal("inbox.sync.required"),
        eventId: z.uuid(),
      })
      .strict();
    return this.subscribeChannel(
      "/inbox-ws-tickets",
      "/inbox-realtime",
      {},
      onSync,
      onState,
      (value) => signal.safeParse(value).success,
    );
  }
  private subscribeChannel(
    ticketPath: string,
    socketPath: string,
    input: Record<string, unknown>,
    onSync: () => void,
    onState: (state: "connecting" | "connected" | "reconnecting") => void,
    isSignal: (value: unknown) => boolean,
  ): () => void {
    const epoch = this.sessionEpoch;
    const lifecycle = { stopped: false };
    const inactive = () => lifecycle.stopped || epoch !== this.sessionEpoch;
    let socket: WebSocket | undefined,
      timer: ReturnType<typeof setTimeout> | undefined,
      attempt = 0;
    const connect = async () => {
      if (inactive()) return;
      onState(attempt ? "reconnecting" : "connecting");
      try {
        const value = z
          .object({ ticket: z.string() })
          .parse(await this.request(ticketPath, "POST", input));
        if (inactive()) return;
        const url = new URL(
          `${this.options.apiBase}${socketPath}`,
          location.href,
        );
        url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
        url.searchParams.set("ticket", value.ticket);
        socket = new WebSocket(url);
        socket.onopen = () => {
          if (inactive()) {
            socket?.close();
            return;
          }
          attempt = 0;
          onState("connected");
          onSync();
        };
        socket.onmessage = (event) => {
          if (inactive()) return;
          if (event.data === "pong") return;
          try {
            if (typeof event.data === "string" && isSignal(JSON.parse(event.data))) onSync();
          } catch {
            /* ignore malformed notification; polling still replays */
          }
        };
        socket.onclose = () => {
          if (inactive()) return;
          onSync();
          retry();
        };
        socket.onerror = () => socket?.close();
      } catch {
        retry();
      }
    };
    const retry = () => {
      if (inactive()) return;
      attempt++;
      onState("reconnecting");
      timer = setTimeout(
        () => void connect(),
        Math.min(30000, 1000 * 2 ** Math.min(attempt, 5)) + Math.random() * 500,
      );
    };
    const online = () => {
      if (!lifecycle.stopped && epoch === this.sessionEpoch) onSync();
    };
    window.addEventListener("online", online);
    void connect();
    return () => {
      lifecycle.stopped = true;
      if (timer) clearTimeout(timer);
      socket?.close();
      window.removeEventListener("online", online);
    };
  }
  clearSession() {
    this.sessionEpoch++;
    this.session = null;
    // A new scope must not wait for an obsolete provider request. Its eventual
    // completion remains rejected by epoch and cannot clear the new flight.
    this.sessionFlight = null;
  }
  async projectHolidays() {
    return projectHolidaysSchema.parse(await this.request("/project-holidays"));
  }
  async updateProjectHolidays(expectedVersion: number, holidayDates: string[]) {
    const input = holidayUpdateSchema.parse({ expectedVersion, holidayDates });
    return projectHolidaysSchema.parse(
      await this.request("/admin/project-holidays", "PUT", input),
    );
  }
  async jobUsage() {
    return jobUsageSchema.parse(await this.request("/admin/job-usage"));
  }
  async connectorKey() {
    return connectorKeyStateSchema.parse(
      await this.request("/admin/connector-key"),
    );
  }
  async rotateConnectorKey(expectedVersion: number) {
    return connectorKeyIssuedSchema.parse(
      await this.request(
        "/admin/connector-key",
        "POST",
        connectorKeyCommandSchema.parse({ expectedVersion }),
      ),
    );
  }
  async revokeConnectorKey(expectedVersion: number) {
    return connectorKeyStateSchema.parse(
      await this.request(
        "/admin/connector-key",
        "DELETE",
        connectorKeyCommandSchema.parse({ expectedVersion }),
      ),
    );
  }
  async admissionBudget() {
    return admissionBudgetSchema.parse(await this.request("/admin/admission"));
  }
  async updateAdmissionBudget(input: z.infer<typeof admissionUpdateSchema>) {
    return admissionBudgetSchema.parse(
      await this.request(
        "/admin/admission",
        "PUT",
        admissionUpdateSchema.parse(input),
      ),
    );
  }
  async downloadUsage() {
    return downloadUsageSchema.parse(
      await this.request("/admin/download-usage"),
    );
  }
  async attachmentUsage() {
    return attachmentUsageSchema.parse(
      await this.request("/admin/attachment-usage"),
    );
  }
  async messageUsage() {
    return messageUsageSchema.parse(await this.request("/admin/usage"));
  }
  async logout() {
    try {
      await this.request("/session", "DELETE");
    } finally {
      this.clearSession();
    }
  }
}
